import React, { memo, useEffect, useState } from 'react';
import { Box, Button, Popover, Typography, useTheme, Stack } from '@mui/material';
import { Add } from "@mui/icons-material";
import i18n from 'translation';
import { enqueueSnackbar } from 'notistack';
import { closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';

import { DashboardPage, DashboardPageInterface } from "routes/dashboard/DashboardPage";
import PageEditDialog from "components/widgetParts/PageEditDialog";
import useFetchData from 'system/useFetchData';
import { useDashboardPageList } from 'routes/dashboard/Dashboard';
import { DashboardDocument } from "components/resources/dashboards/DashboardDocument";
import { useTemplateQueries } from 'routes/dashboard/DashboardPageRoot';
import { DashboardPageListRow } from './DashboardPageListRow';
import APIRequest, { APIError } from 'system/APIRequest';
import { getDashboardNavigatePath, parsePageParams } from 'components/resources/dashboards/DashboardNavigationUtils';

interface DashboardPageSelectorOptions {
  dashboardId ?: string,
  activePageId ?: string,
  handleUpdatePageName?: (name: string) => void;
}

export function sortAndCombinePages(dashboard: DashboardDocument, pageEntries: Array<DashboardPage>): Array<DashboardPage> {
  if (!dashboard?.data?.pages || !pageEntries) {
    return [];
  }
  const allPages = [...pageEntries];

  const dashboardPageUUIDs = dashboard.data.pages;
  // Sort the responses to match the order of the UUIDs
  allPages?.sort((a:DashboardPage, b:DashboardPage) => {
    if (!dashboardPageUUIDs) {
      return 0;
    }
    // ensure rows with no id found are at the end (generally they are new)
    if (!a.uuid || !dashboardPageUUIDs.includes(a.uuid)) {
      return 1;
    }
    if (!b.uuid || !dashboardPageUUIDs.includes(b.uuid)) {
      return -1;
    }
    const aIndex = dashboardPageUUIDs.indexOf(a.uuid);
    const bIndex = dashboardPageUUIDs.indexOf(b.uuid);
    return aIndex - bIndex;
  });
  return allPages;
}

function DashboardPageSelector(props:  DashboardPageSelectorOptions): JSX.Element {
  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [newPageDialogIsOpen, setNewPageDialogIsOpen] = useState<boolean>(false);
  const [activePageEditID, setActivePageEditID] = useState<string | null>(null);
  const { dashboardPages, handleRefreshPageList } = useDashboardPageList();
  const [localDashboardPages, setLocalDashboardPages] = useState<DashboardPageInterface[] | []>(dashboardPages);
  const theme = useTheme();

  useEffect(() => {
    if (localDashboardPages.length > 0) return;
    setLocalDashboardPages(dashboardPages);
  }, [dashboardPages]);

  // Load the current dashboard
  const [dashboardResponse, /*unused loading flag*/, /*unused error response*/, handleRefresh] = useFetchData(`obj/dashboard/${props.dashboardId}`, []);
  const dashboard : DashboardDocument =  dashboardResponse?.output || {};

  const templateQueriesContext = useTemplateQueries(),
        templateQueries = templateQueriesContext.templateQueries;

  // dashboardLink
  let dashboardLink = `/dashboard/${dashboard?.id}`;
  if (templateQueries?.length) {
    const localQueryIds = templateQueries.map((query) => query.id);
    if (templateQueriesContext.globalQuery) {
      localQueryIds.push(`g_${templateQueriesContext.globalQuery.id}`);
    }
    const queryIds = localQueryIds.join('-');
    dashboardLink = `/dashboard/${dashboard?.id}/${queryIds}`;
  }

  const activePage = dashboardPages?.find((page: DashboardPageInterface) => {
    return page.id === props.activePageId;
  });

  const popoverIsOpen = Boolean(anchorEl);
  const popoverId = popoverIsOpen ? 'simple-popover' : undefined;

  let truncatedPageName = activePage?.name;
  if (activePage?.name && activePage?.name.length > 20) {
    truncatedPageName = activePage?.name.substring(0, 20) + "...";
  }

  const sortablePageIdItems: string[] = dashboardPages.map(page => page.id);
  // DndContext uses sensors to manage when & how dragging activates
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleSetLocalDashboardPages(newLocalDashboardPages: DashboardPageInterface[]) {
    setLocalDashboardPages(newLocalDashboardPages);
  }

  function movePage(page: DashboardPageInterface, moveUp: boolean) {
    // move the page UUID in the list of pages in the dashboard
    const pageUUIDs = dashboardPages.map((page:DashboardPageInterface) => page.uuid);
    const pageIndex = pageUUIDs?.indexOf(page.uuid);
    
    if (!pageUUIDs || (moveUp && pageIndex === 0) || (!moveUp && pageIndex === pageUUIDs.length - 1)) {
      return;
    }

    const newPageUUIDs = [...pageUUIDs];
    const destIndex = moveUp ? pageIndex - 1 : pageIndex + 1;

    // Remove original page & insert it at new index
    newPageUUIDs.splice(pageIndex, 1);
    newPageUUIDs.splice(destIndex, 0, page.uuid);
    dashboard.data.pages = newPageUUIDs;

    // Updating the local dashboard pages state to reflect change in UI w/o refresh
    let localDashboardPagesCopy = localDashboardPages.toSpliced(pageIndex, 1);
    localDashboardPagesCopy.splice(destIndex, 0, localDashboardPages[pageIndex]);
    // Using optimistic rendering, show change in UI before updating API
    setLocalDashboardPages(localDashboardPagesCopy);

    APIRequest(`obj/dashboard/${dashboard.id}`, {}, {
      method: "PUT",
      "body": JSON.stringify({
        data: dashboard.data
      })
    }).then(() => {
      handleRefreshPageList();
    }).catch((error: APIError) => {
      // If page reordering errors, revert back to previous state
      setLocalDashboardPages(dashboardPages);
      enqueueSnackbar(`${i18n.t("dashboard.edit-pages.moving-page-error")}: ${error.message}`, { variant: 'error', persist: true });
    });
  }

  function movePageUp(page: DashboardPageInterface) {
    movePage(page, true);
  }

  function movePageDown(page: DashboardPageInterface) {
    movePage(page, false);
  }

  // function that deletes an existing page via the API
  function deletePage(pageToDelete: DashboardPageInterface) {
    if (!pageToDelete || !pageToDelete.id) {
      enqueueSnackbar(i18n.t("dashboard.delete-dashboard-error"), { variant: 'error', persist: true });
      return;
    }
    if (pageToDelete?._deletable === false) {
      enqueueSnackbar(i18n.t("account-status.insufficient-account-permissions"), { variant: "error"});
    }

    // Delete the id from the dashboard data
    if (!dashboard?.data) {
      dashboard.data = { pages: [] };
    }
    if (!dashboard?.data?.pages) {
      dashboard.data.pages = [];
    }
    dashboard.data.pages = dashboard.data.pages.filter((pageUUID:string) => {
      return pageUUID !== pageToDelete.uuid;
    });

    // Update the dashboard
    APIRequest(`obj/dashboard/${dashboard?.id}`, {}, {
      method: "PUT",
      "body": JSON.stringify({
        data: dashboard.data
      })
    }).then((response: any) => {
      // Delete the page
      APIRequest(`obj/dashboard-page/${pageToDelete.id}`, {}, {
        method: "DELETE",
      }).then((response: any) => {
        enqueueSnackbar(`${i18n.t("dashboard.edit-pages.delete-page-success")}: ${pageToDelete.name}`, { variant: 'success' });
        if (response.success) {
          const pageParams = parsePageParams();
          let newPageId = "";
          if (activePage && (activePage.id === pageToDelete.id)) {
            // We are deleting the current page.
            for (const page of dashboardPages) {
              if (page.id !== pageToDelete.id) {
                // Grab the first page that isn't the page being deleted.
                newPageId = page.id;
                break;
              }
            }
          } else {
            // Keep the same active page.
            newPageId = activePage?.id || "";
          }
          pageParams.pageId = newPageId;

          const navigatePath = getDashboardNavigatePath(pageParams);
          window.location.href = navigatePath;
        }
      }).catch((error: APIError) => {
        enqueueSnackbar(`${i18n.t("dashboard.edit-pages.delete-page-error")}: ${error.message}`, { variant: 'error', persist: true });
      });
    }).catch((error: APIError) => {
      enqueueSnackbar(`${i18n.t("dashboard.edit-pages.delete-page-error")}: ${error.message}`, { variant: 'error', persist: true });
    });
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    const foundPage = dashboardPages.find(pages => pages.id === active.id);

    if (!over || (over.id === active.id) || !foundPage) {
      return;
    }

    const oldIndex = sortablePageIdItems.indexOf(active.id.toString());
    const newIndex = sortablePageIdItems.indexOf(over.id.toString());

    // Remove original page & insert it at new index
    const pageUUIDs = dashboardPages.map((page: DashboardPageInterface) => page.uuid);
    const newPageUUIDs = pageUUIDs.toSpliced(oldIndex, 1);
    newPageUUIDs.splice(newIndex, 0, foundPage.uuid);      
    dashboard.data.pages = newPageUUIDs;

    // Optimistically rerender the state & UI
    let localDashboardPagesCopy = localDashboardPages.toSpliced(oldIndex, 1);
    localDashboardPagesCopy.splice(newIndex, 0, localDashboardPages[oldIndex]);
    setLocalDashboardPages(localDashboardPagesCopy);

    APIRequest(`obj/dashboard/${dashboard.id}`, {}, {
      method: "PUT",
      "body": JSON.stringify({
        data: dashboard.data
      })
    }).then(() => {
      handleRefreshPageList();
    }).catch((error: APIError) => {
      // If optimistic rerender fails, reset to previous dashboardPages state
      setLocalDashboardPages(dashboardPages);
      enqueueSnackbar(`${i18n.t("dashboard.edit-pages.moving-page-error")}: ${error.message}`, { variant: 'error', persist: true });
    });
  }

  return (
    <>
      <Button
        sx={{ background: `${theme.colors.primary.lighter}` }}
        variant="outlined"
        aria-describedby={popoverId}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          if (anchorEl) {
            setAnchorEl(null);
          } else {
            setAnchorEl(event.currentTarget);
          }
        }}
      >
        <Typography variant="body1">{i18n.t("dashboard.edit-pages.page")}</Typography>
        <span>
          {truncatedPageName &&
            <>: {truncatedPageName}</>
          }
        </span>
      </Button>
      <Popover
        id={popoverId}
        open={popoverIsOpen}
        anchorEl={anchorEl}
        onClose={() => { setAnchorEl(null); }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <Stack
          sx={{
            padding: 2,
            paddingTop: 1,
            paddingBottom: 1,
            gap: 1,
            background: theme.palette.background.default
          }}>
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            sensors={sensors}
          >
            <SortableContext
              items={sortablePageIdItems}
              strategy={verticalListSortingStrategy}
            >
              {localDashboardPages?.map((page: DashboardPageInterface) => {
                return (
                  <DashboardPageListRow
                    id={page.id}
                    key={page.id}
                    dashboard={dashboard}
                    page={page}
                    activePageId={props.activePageId}
                    dashboardLink={dashboardLink}
                    closeMenu={() => { setAnchorEl(null); }}
                    setActivePageEditID={setActivePageEditID}
                    setNewPageDialogIsOpen={setNewPageDialogIsOpen}
                    handleRefreshPageList={handleRefreshPageList}
                    movePageUp={movePageUp}
                    movePageDown={movePageDown}
                    deletePage={deletePage}
                  />
                );
              })}
            </SortableContext>
          </DndContext>
        </Stack>
        {dashboard?._modifiable &&
          <Box
            sx={{
              textAlign: "center",
              padding: 2,
              paddingTop: 1,
              paddingBottom: 1
            }}>
            <Button
              variant="contained"
              size="small"
              endIcon={<Add />}
              color="primary"
              onClick={(e) => {
                e.preventDefault();
                setActivePageEditID(null);
                setNewPageDialogIsOpen(true)
              }}
            >
              {i18n.t("add-new")}
            </Button>
          </Box>
        }
      </Popover>
      {newPageDialogIsOpen && dashboard && dashboard?._modifiable &&
        <PageEditDialog
          activePageID = {activePageEditID}
          dashboard={dashboard}
          setDialogIsOpen = {setNewPageDialogIsOpen}
          navigationRoot = {dashboardLink}
          onSetLocalDashboardPages={handleSetLocalDashboardPages}
          handleUpdatePageName = {props.handleUpdatePageName}
          onClose = {() => {
            setActivePageEditID(null);
            handleRefresh();
            handleRefreshPageList();
          }}
        />
      }
    </>
  );
}

export default memo(DashboardPageSelector);